import { useState, type ReactNode } from 'react'
import { Button, Input, Select, SkeletonStack } from '@/shared/ui'
import { useAccount } from '@/entities/session'
import { useLogout } from '@/features/auth'
import { useUpdateCompany } from '@/features/company-edit/model'
import { TIMEZONES } from '@/shared/config/timezones'
import { formatDateTime, nowInTimezone } from '@/shared/lib/time'
import { ApiError } from '@/shared/api/client'
import type { Account } from '@/shared/api/types'
import styles from './ui/CompanyPage.module.css'
import { PasswordForm } from './PasswordForm'

export function CompanyPage() {
  const { data: account, isPending } = useAccount()

  return (
    <>
      {isPending ? (
        <div className={styles.panel}>
          <div style={{ padding: 'var(--space-5)' }}>
            <SkeletonStack rows={3} height={36} />
          </div>
        </div>
      ) : account ? (
        <>
          <CompanySettings account={account} />
          <AccountInfo account={account} />
          <PasswordForm />
          <DangerZone />
        </>
      ) : null}
    </>
  )
}

function CompanySettings({ account }: { account: Account }) {
  const [name, setName] = useState(account.company.name)
  const [timezone, setTimezone] = useState(account.company.timezone)
  const [callerNumber, setCallerNumber] = useState(account.company.callerNumber)
  const update = useUpdateCompany()

  const error = update.error instanceof ApiError ? update.error : undefined
  const dirty =
    name !== account.company.name ||
    timezone !== account.company.timezone ||
    callerNumber !== account.company.callerNumber

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>Данные компании</div>

      <Row label="Название" hint="Как компания называется у нас в кабинете">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          invalid={error?.field === 'name'}
        />
      </Row>

      <Row
        label="Часовой пояс"
        // Текущее время рядом с выбором — самая быстрая проверка, что
        // пояс верный. По «Asia/Yekaterinburg» этого не понять, а по
        // «сейчас 14:32» понятно сразу.
        hint={`Сейчас у вас ${nowInTimezone(timezone)}. По этому времени агент решает, можно ли звонить`}
      >
        <Select
          options={TIMEZONES.map((tz) => ({ value: tz.value, label: tz.label }))}
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          invalid={error?.field === 'timezone'}
        />
      </Row>

      <Row
        label="Номер, с которого звоним"
        hint="Этот номер увидит клиент. Пусто — общий номер +7 904 953-70-14"
        error={error?.field === 'callerNumber' ? error.message : undefined}
      >
        <Input
          value={callerNumber}
          onChange={(e) => setCallerNumber(e.target.value)}
          placeholder="+7 900 000-00-00"
          inputMode="tel"
          invalid={error?.field === 'callerNumber'}
        />
      </Row>

      <div className={styles.actions}>
        <Button
          size="sm"
          loading={update.isPending}
          disabled={!dirty}
          onClick={() => update.mutate({ name: name.trim(), timezone, callerNumber: callerNumber.trim() })}
        >
          Сохранить
        </Button>
        {dirty && <span className={styles.dirty}>Есть несохранённые правки</span>}
        {error && !error.field && <span className={styles.dirty}>{error.message}</span>}
      </div>
    </div>
  )
}

function AccountInfo({ account }: { account: Account }) {
  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>Ваш аккаунт</div>

      <Row label="Почта">
        <span className={`${styles.value} ${styles.mono}`}>{account.user.email}</span>
      </Row>

      <Row label="Имя">
        <span className={styles.value}>{account.user.name || '—'}</span>
      </Row>

      <Row label="Роль">
        <span className={styles.role}>
          {account.user.role === 'owner' ? 'Владелец' : account.user.role}
        </span>
      </Row>

      <Row label="Последний вход">
        <span className={`${styles.value} ${styles.mono}`}>
          {formatDateTime(account.user.lastLoginAt)}
        </span>
      </Row>

      <Row label="Компания заведена">
        <span className={`${styles.value} ${styles.mono}`}>
          {formatDateTime(account.company.createdAt)}
        </span>
      </Row>
    </div>
  )
}

function DangerZone() {
  const logout = useLogout()

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>Доступ</div>

      <div className={styles.danger}>
        <p className={styles.dangerText}>
          Выход завершит сессию в этом браузере. Настройки, звонки и записи останутся на
          месте — войти можно тем же паролем.
        </p>
        <Button
          variant="secondary"
          size="sm"
          loading={logout.isPending}
          onClick={() => logout.mutate()}
        >
          Выйти
        </Button>
      </div>
    </div>
  )
}

/** Строка настройки: подпись с пояснением слева, значение справа. */
function Row({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: string
  error?: string
  children: ReactNode
}) {
  return (
    <div className={styles.row}>
      <div>
        <div className={styles.label}>{label}</div>
        {hint && <div className={styles.hint}>{hint}</div>}
      </div>
      <div>
        {children}
        {error && <div className={styles.fieldError}>{error}</div>}
      </div>
    </div>
  )
}
